/**
 * Product Image Lightbox
 * Opens product images in a centered modal/popup when clicked
 */

class ProductImageLightbox {
  constructor() {
    this.currentIndex = 0;
    this.images = [];
    this.lightbox = null;
    
    this.init();
  }

  init() {
    // Create lightbox HTML structure
    this.createLightbox();
    
    // Add click handlers to product images
    this.attachClickHandlers();
    
    // Add keyboard navigation
    this.addKeyboardNavigation();
  }

  createLightbox() {
    const lightboxHTML = `
      <div id="product-image-lightbox" class="product-lightbox" style="display: none;">
        <div class="lightbox-overlay"></div>
        <div class="lightbox-container">
          <div class="lightbox-header">
            <div class="lightbox-counter"></div>
            <div class="lightbox-controls">
              <button class="lightbox-fullscreen" aria-label="Fullscreen" title="Fullscreen">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                </svg>
              </button>
              <button class="lightbox-share" aria-label="Share" title="Share">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                </svg>
              </button>
              <button class="lightbox-close" aria-label="Close" title="Close">&times;</button>
            </div>
          </div>
          <button class="lightbox-prev" aria-label="Previous">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <button class="lightbox-next" aria-label="Next">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
          <div class="lightbox-content">
            <div class="lightbox-image-wrapper">
              <img src="" alt="" class="lightbox-image">
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', lightboxHTML);
    this.lightbox = document.getElementById('product-image-lightbox');

    // Add event listeners
    this.lightbox.querySelector('.lightbox-close').addEventListener('click', () => this.close());
    this.lightbox.querySelector('.lightbox-overlay').addEventListener('click', () => this.close());
    this.lightbox.querySelector('.lightbox-prev').addEventListener('click', () => this.prev());
    this.lightbox.querySelector('.lightbox-next').addEventListener('click', () => this.next());
    this.lightbox.querySelector('.lightbox-fullscreen').addEventListener('click', () => this.toggleFullscreen());
    this.lightbox.querySelector('.lightbox-share').addEventListener('click', () => this.share());
    
    // Click on the content area (padding areas) to close
    this.lightbox.querySelector('.lightbox-content').addEventListener('click', (e) => {
      if (e.target.classList.contains('lightbox-content') || e.target.classList.contains('lightbox-image-wrapper')) {
        this.close();
      }
    });
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      this.lightbox.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  share() {
    const currentImage = this.images[this.currentIndex];
    if (navigator.share) {
      navigator.share({
        title: currentImage.alt,
        text: 'Check out this product image',
        url: window.location.href
      }).catch(() => {});
    } else {
      // Fallback: copy link to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  }

  attachClickHandlers() {
    // Target product images in the featured slider
    const productMedia = document.querySelector('product-media');
    if (!productMedia) return;

    // Get all clickable product images - improved selector
    const imageElements = productMedia.querySelectorAll('.product-featured img:not(.slick-cloned), .slider-featured img:not(.slick-cloned)');
    
    this.images = Array.from(imageElements).map(img => {
      // Get the highest resolution image available
      let imgSrc = img.src;
      
      // Priority 1: Check for data-src (often highest quality)
      if (img.dataset.src) {
        imgSrc = img.dataset.src;
      }
      // Priority 2: Check for data-zoom or data-large
      else if (img.dataset.zoom) {
        imgSrc = img.dataset.zoom;
      }
      else if (img.dataset.large) {
        imgSrc = img.dataset.large;
      }
      // Priority 3: Parse srcset for highest resolution
      else if (img.srcset) {
        const srcsetArray = img.srcset.split(',');
        imgSrc = srcsetArray[srcsetArray.length - 1].trim().split(' ')[0];
      }
      
      // Force maximum Shopify image size (or similar platform)
      // Replace any size modifier with the largest available
      imgSrc = imgSrc.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|1024x1024|2048x2048)\./gi, '_master.');
      
      // If no master, try 2048x or original
      if (!imgSrc.includes('_master.')) {
        imgSrc = imgSrc.replace(/\.(jpg|jpeg|png|webp)/gi, '_2048x.$1');
      }
      
      return {
        src: imgSrc,
        alt: img.alt || 'Product image'
      };
    });

    // Add click handlers to ALL images
    imageElements.forEach((img, index) => {
      img.style.cursor = 'pointer';
      img.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open(index);
      });
    });
  }

  open(index = 0) {
    this.currentIndex = index;
    this.updateImage();
    this.lightbox.style.display = 'block';
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
    
    // Fade in animation
    setTimeout(() => {
      this.lightbox.classList.add('active');
    }, 10);
  }

  close() {
    this.lightbox.classList.remove('active');
    document.body.style.overflow = ''; // Restore scrolling
    
    setTimeout(() => {
      this.lightbox.style.display = 'none';
    }, 300);
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    this.updateImage();
  }

  prev() {
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    this.updateImage();
  }

  updateImage() {
    const img = this.lightbox.querySelector('.lightbox-image');
    const counter = this.lightbox.querySelector('.lightbox-counter');
    
    if (this.images[this.currentIndex]) {
      img.src = this.images[this.currentIndex].src;
      img.alt = this.images[this.currentIndex].alt;
      counter.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
    }

    // Show/hide navigation buttons
    const prevBtn = this.lightbox.querySelector('.lightbox-prev');
    const nextBtn = this.lightbox.querySelector('.lightbox-next');
    
    if (this.images.length <= 1) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
    } else {
      prevBtn.style.display = 'flex';
      nextBtn.style.display = 'flex';
    }
  }

  addKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (this.lightbox.style.display === 'none') return;
      
      switch(e.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          this.prev();
          break;
        case 'ArrowRight':
          this.next();
          break;
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductImageLightbox();
  });
} else {
  new ProductImageLightbox();
}